'use client';

import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { MessageSquare, Send } from 'lucide-react';
import { format } from 'date-fns';
import { showToast } from '@/lib/toast';
import axios from 'axios';
import { signIn, useSession } from 'next-auth/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form';

export default function CommentSection({ referenceId, type }) {
  const { data: session, update } = useSession();
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(false);

  // Pagination + Comments
  const [comments, setComments] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [fetchingComments, setFetchingComments] = useState(false);

  // Display Name Modal
  const [showDisplayNameModal, setShowDisplayNameModal] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [pendingComment, setPendingComment] = useState(null);

  const formSchema = z.object({
    displayName: z.string().min(2, { message: 'Display name must be at least 2 characters.' }),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { displayName: '' },
  });

  const fetchComments = async (cursor = null, append = false) => {
    if (!referenceId || !type) return;
    setFetchingComments(true);

    try {
      const params = { referenceId: referenceId, type };
      if (cursor) params.cursor = cursor;

      const res = await axios.get(`${process.env.NEXT_PUBLIC_API_URL}/comments/list`, { params });

      if (res?.data?.success) {
        const data = res.data.data || [];
        const meta = res.data.meta || {};

        setComments((prev) => (append ? [...prev, ...data] : data));

        setNextCursor(meta.nextCursor || null);
        setHasNextPage(Boolean(meta.hasNextPage));
      } else {
        showToast('Failed to load comments', 'error');
      }
    } catch (error) {
      // showToast('Failed to load comments', 'error');
    } finally {
      setFetchingComments(false);
    }
  };

  useEffect(() => {
    setComments([]);
    setNextCursor(null);
    setHasNextPage(false);
    fetchComments();
  }, [referenceId, type]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!session) {
      signIn(undefined, { callbackUrl: window.location.href });
      return;
    }

    if (!session?.user?.displayName) {
      setPendingComment(commentText);
      setShowDisplayNameModal(true);
      return;
    }

    if (!commentText.trim()) {
      showToast('Please enter a comment', 'error');
      return;
    }

    setLoading(true);
    try {
      const token = session?.accessToken;
      const payload = { referenceId: referenceId, type, content: commentText };

      const res = await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/comments`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res?.data?.success) {
        showToast('Comment posted successfully', 'success');
        setCommentText('');
        fetchComments();
      } else {
        showToast('Failed to post comment', 'error');
      }
    } catch (error) {
      showToast('Failed to post comment', 'error');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitDisplayName = async (formData) => {
    setSavingName(true);
    try {
      const token = session?.accessToken;
      const payload = { displayName: formData.displayName.trim() };

      const baseUrl = process.env.NEXT_PUBLIC_API_URL;
      const url =
        session?.user?.role === 'professional'
          ? `${baseUrl}/professionals/profile`
          : `${baseUrl}/users/profile`;

      const res = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        await update({
          displayName: data?.data?.displayName || formData.displayName,
        });

        showToast('Display name updated successfully', 'success');
        setShowDisplayNameModal(false);

        if (pendingComment) {
          const commentToPost = pendingComment;
          setPendingComment(null);
          setCommentText('');
        }
      } else {
        form.setError('displayName', { message: data?.message });
      }
    } catch (err) {
      showToast('An unexpected error occurred', 'error');
    } finally {
      setSavingName(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;

    return date.toLocaleDateString();
  };

  return (
    <>
      <Card className="rounded-xl border-0 shadow-sm">
        <CardHeader className="gap-0">
          <div className="flex items-center justify-start gap-x-3">
            <div className="from-primary/20 to-primary/10 flex size-10 items-center justify-center rounded-xl bg-gradient-to-br shadow-sm">
              <MessageSquare className="text-primary size-5" />
            </div>

            <div className="flex-1">
              <CardTitle className="gap-0 text-xl font-bold md:text-2xl">
                {type === 'BusinessProfile' ? 'Leave a Review' : 'Leave a Comment'}
              </CardTitle>
              <CardDescription className="text-sm text-gray-600">
                {type === 'BusinessProfile'
                  ? 'Know the business? Please write a review. Once approved, it will go live.'
                  : 'Share your thoughts. Once approved, they will go live.'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className={'flex flex-col gap-y-6 px-6'}>
          {!session && (
            <div className="flex flex-col items-start text-start">
              <p className="text-primary text-lg font-semibold">Sign in to share your thoughts</p>
              <Button
                onClick={() => signIn(undefined, { callbackUrl: window.location.href })}
                className="bg-primary hover:bg-primary/90 mt-3 rounded-lg px-8 py-1 font-semibold text-white transition-all"
              >
                Sign In to Comment
              </Button>
            </div>
          )}

          {session && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2.5">
                <label
                  htmlFor="comment"
                  className="flex items-center gap-2 text-sm font-semibold text-gray-700"
                >
                  Your Comment
                  <span className="text-destructive text-xs">*</span>
                </label>
                <Textarea
                  id="comment"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={
                    type === 'Blog'
                      ? 'Share your perspective...'
                      : 'Share your experience with this business'
                  }
                  className="focus:border-primary/50 focus:ring-primary/10 min-h-[140px] resize-none rounded-xl border-2 border-gray-200/80 shadow-sm transition-all duration-200 hover:shadow-md focus:ring-4"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 h-10 w-full rounded-xl bg-gradient-to-r px-10 font-semibold text-white transition-all duration-300 hover:scale-[1.02] hover:shadow-xl md:w-auto"
              >
                <Send className="mr-2 size-4" />
                {loading ? 'Posting...' : 'Post Comment'}
              </Button>
            </form>
          )}

          {comments.length > 0 && (
            <>
              <div>
                <Separator className="" />
              </div>

              <div className="flex flex-col gap-y-5">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-bold text-gray-900 md:text-2xl">Comments</h3>
                  <Badge className="bg-primary/10 text-primary size-7 rounded-full font-semibold">
                    {comments.length}
                  </Badge>
                </div>

                <div className="space-y-1">
                  {comments.map((comment) => (
                    <div
                      key={comment._id}
                      className="border-b-1 border-b-gray-100 bg-white px-3 py-3 transition-colors duration-150 last:border-b-0 hover:bg-gray-50"
                    >
                      <div className="flex items-start gap-4">
                        <Avatar className="h-9 w-9 ring-1 ring-gray-100">
                          <AvatarFallback className="from-primary/60 to-primary/100 bg-gradient-to-br text-sm font-semibold text-white">
                            {comment?.author?.firstName
                              ? comment?.author.firstName.charAt(0).toUpperCase()
                              : 'U'}
                          </AvatarFallback>
                        </Avatar>

                        <div>
                          <div className="flex items-center gap-x-2">
                            <h4 className="font-bold">
                              {comment?.author?.displayName
                                ? comment?.author.displayName
                                : comment?.author?.firstName || comment?.author?.lastName
                                  ? `${comment?.author.firstName || ''} ${
                                      comment?.author.lastName || ''
                                    }`.trim()
                                  : 'User'}
                            </h4>

                            <p className="text-xs text-gray-500">{formatTime(comment.createdAt)}</p>
                          </div>

                          <p className="mt-2 text-sm">{comment.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {hasNextPage && (
                  <div className="flex justify-center pt-6">
                    <Button
                      variant="outline"
                      disabled={fetchingComments}
                      onClick={() => fetchComments(nextCursor, true)}
                    >
                      {fetchingComments ? 'Loading...' : 'Load More Comments'}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/*  Display Name Modal */}
      <Dialog open={showDisplayNameModal} onOpenChange={setShowDisplayNameModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Your Display Name</DialogTitle>
          </DialogHeader>

          <p className="mb-4 text-sm text-gray-600">
            Please enter a display name before posting your comment.
          </p>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmitDisplayName)} className="space-y-4">
              <FormField
                control={form.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input placeholder="Enter your display name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="mt-4 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDisplayNameModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={savingName}>
                  {savingName ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
